import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Archive, Download, FileText, Paperclip, Plus, Search, X } from 'lucide-react';
import { api, qs } from '@/lib/api';
import { usePagedList } from '@/hooks/usePagedList';
import { ListFooter } from '@/features/common/ListFooter';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { useIsPhone } from '@/hooks/useIsPhone';
import { financialYearOf, financialYearPeriod, financialYearQuarters, formatDate, quickPeriods, type Period } from '@/lib/dates';
import { useFiscalYearStartMonth, useToday } from '@/hooks/useOrgSettings';
import { BULK_PDF_MAX } from '@fint/validation';
import { PageHeader } from '@/app/DashboardLayout';
import type { DocumentStatus } from '@fint/validation';
import {
  Button, Card, CardFacts, CardLine, CardRow, Cards, ColourChip, EmptyState, ErrorState, Input, Money,
  SkeletonTable, StatusBadge, TBody, TD, TH, THead, TR, Table,
} from '@/features/common/ui';
import { useProjects, useTags, type TagOption } from '@/features/common/Classify';
import { matchesExternalNumber, originallyTitle, type ImportedDocument } from './imported';
import { AttachOriginalsSheet } from './AttachOriginalsSheet';
import { AdvancedFilter, useAdvancedFilter, type AdvancedFilterValue } from '@/features/common/AdvancedFilter';

interface InvoiceRow extends ImportedDocument {
  id: string;
  number: string;
  customerName: string;
  date: string;
  dueDate: string | null;
  currency: string;
  total: string;
  balanceDue: string;
  status: DocumentStatus;
  projectId: string | null;
  projectName: string | null;
  tags: TagOption[];
}

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Drafts' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'paid', label: 'Paid' },
] as const;

export function InvoicesPage() {
  const org = useActiveOrg();
  const perms = usePermissions();
  const isPhone = useIsPhone();
  // Filters live in the URL so a refresh keeps them and the view can be pasted
  // to a colleague.
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') ?? 'all';
  const [search, setSearch] = useState(searchParams.get('q') ?? '');
  const [attachingOriginals, setAttachingOriginals] = useState(false);
  /**
   * Project and tag, in the URL alongside the status filter rather than in
   * component state.
   *
   * The API takes them, so "the unpaid invoices on the Acme rebuild" is a link
   * - which is the form that question actually gets asked in. `projectId=none`
   * carries the literal the API defines for "not on a project"; it is a value,
   * not an omission, and the two are different lists.
   */
  const projectId = searchParams.get('projectId') ?? '';
  const tagIds = searchParams.getAll('tag');
  const tagMatch = searchParams.get('tagMatch') === 'all' ? 'all' : 'any';
  // The condition builder - `?where=` / `?savedFilterId=` - on top of the
  // named filters, in the same URL.
  const advanced = useAdvancedFilter();

  /**
   * ── THE PERIODS A PERSON ASKS FOR ──
   * "September's invoices for the accountant" is the request this page gets
   * most, and the condition builder answers it in five clicks. These chips
   * answer it in one - and write the SAME condition the builder would ("Invoice
   * date is between …"), so it shows as the same removable chip, saves as a
   * filter like any other, and leaves the other conditions as they were.
   */
  const today = useToday();
  const startMonth = useFiscalYearStartMonth();
  const periods = useMemo(() => invoicePeriods(today, startMonth), [today, startMonth]);
  const dateCondition = advanced.value.definition?.conditions.find((c) => c.field === 'date' && c.operator === 'between');
  const activePeriod = periods.find((period) => {
    const value = dateCondition?.value as [string, string] | undefined;
    return !!value && value[0] === period.from && value[1] === period.to;
  });
  const setPeriod = (period: Period | null) => {
    // A saved filter is replaced, not edited: it is somebody's named view,
    // and a chip that quietly changed it would change it for everybody.
    const others = advanced.value.savedFilterId
      ? []
      : (advanced.value.definition?.conditions ?? []).filter((c) => !(c.field === 'date' && c.operator === 'between'));
    const conditions = period ? [...others, { field: 'date', operator: 'between' as const, value: [period.from, period.to] }] : others;
    const next: AdvancedFilterValue = conditions.length
      ? { definition: { conjunction: advanced.value.definition?.conjunction ?? 'and', conditions }, savedFilterId: null }
      : { definition: null, savedFilterId: null };
    advanced.set(next);
  };

  /**
   * Ticked invoices, for one download. Dropped whenever the filter changes:
   * a selection made under "September" must not ride along into October.
   */
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
  const filterKey = [status, search, projectId, tagIds.join(','), tagMatch, advanced.key].join('|');
  useEffect(() => setSelectedIds([]), [filterKey]);
  const [downloading, setDownloading] = useState<'pdf' | 'zip' | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const listPath = (extra: { q?: string; cursor?: string }) =>
    `/invoices${qs({
      status,
      cursor: extra.cursor,
      q: extra.q,
      projectId: projectId || undefined,
      // Only once there are two tags to combine. With one, Any and All are
      // the same question and sending either is noise in the cache key.
      tagMatch: tagIds.length > 1 ? tagMatch : undefined,
      ...advanced.params,
    })}${tagIds.map((id) => `&tag=${encodeURIComponent(id)}`).join('')}`;

  const query = usePagedList<InvoiceRow, { scopedToSelf: boolean }>({
    key: ['invoices', org?.id, status, search, projectId, tagIds.join(','), tagMatch, advanced.key],
    path: (cursor) => listPath({ q: search, cursor }),
    enabled: !!org?.id,
  });

  /**
   * ── SEARCHING BY THE NUMBER IT HAD BEFORE ──
   * An imported invoice has two numbers, and the one a person remembers is
   * the one on Zoho's PDF. The server's `q` covers the number, the reference
   * and the customer; whether it covers the external number too is the
   * API's to decide. So when a search comes back with NOTHING, the page asks
   * once more without the term, for the same status and filters, and looks
   * for the term in the external numbers of the invoices that page holds -
   * and says that it did, because "the invoices loaded here" is a smaller
   * promise than a server search. It never fires while the server found
   * something, so a search the API covers costs no second request.
   */
  const term = search.trim();
  const serverFoundNothing = !!term && !query.isLoading && !query.isError && query.items.length === 0;
  const fallback = useQuery({
    // A discriminator at the end: the same prefix as the list, so a confirm
    // elsewhere invalidates it, but never the unfiltered list's own key.
    queryKey: ['invoices', org?.id, status, '', projectId, tagIds.join(','), tagMatch, advanced.key, 'by-external-number'],
    queryFn: () => api.get<{ items: InvoiceRow[] }>(listPath({})),
    enabled: !!org?.id && serverFoundNothing,
  });
  const byExternalNumber = serverFoundNothing
    ? (fallback.data?.items ?? []).filter((row) => matchesExternalNumber(term, row.externalNumber))
    : [];
  const rows = query.items.length ? query.items : byExternalNumber;
  const loading = query.isLoading || (serverFoundNothing && fallback.isLoading);

  const loadedIds = rows.map((row) => row.id);
  const allLoadedSelected = loadedIds.length > 0 && loadedIds.every((id) => selected.has(id));
  const someLoadedSelected = loadedIds.some((id) => selected.has(id));
  const toggleOne = (id: string) =>
    setSelectedIds((current) => (current.includes(id) ? current.filter((held) => held !== id) : [...current, id]));
  const toggleAllLoaded = () =>
    setSelectedIds((current) =>
      allLoadedSelected ? current.filter((id) => !loadedIds.includes(id)) : Array.from(new Set([...current, ...loadedIds])),
    );

  /**
   * The selected invoices as one PDF or a ZIP of PDFs. A file, so it is
   * fetched as one and handed to the browser as a download - the same way the
   * report export is - with the name the server gave it ("Invoices 2026-09-02
   * to 2026-09-30 (11).zip").
   */
  const download = async (format: 'pdf' | 'zip') => {
    if (!org || downloading || !selectedIds.length) return;
    setDownloading(format);
    setDownloadError(null);
    try {
      const res = await fetch('/v1/invoices/bulk-pdf', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json', 'x-org-id': org.id },
        body: JSON.stringify({ ids: selectedIds, format }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string | string[] };
        throw new Error(Array.isArray(body.message) ? body.message.join(' ') : body.message || `The download failed (${res.status}).`);
      }
      const named = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1];
      const url = URL.createObjectURL(await res.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = named || `invoices.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Freed on the next tick: revoking synchronously races the download in Safari.
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (error) {
      setDownloadError(error instanceof Error ? error.message : 'The download failed.');
    } finally {
      setDownloading(null);
    }
  };
  const tooMany = selectedIds.length > BULK_PDF_MAX;

  const setStatus = (next: string) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'all') params.delete('status');
    else params.set('status', next);
    setSearchParams(params, { replace: true });
  };

  const setProject = (next: string) => {
    const params = new URLSearchParams(searchParams);
    if (next) params.set('projectId', next);
    else params.delete('projectId');
    setSearchParams(params, { replace: true });
  };

  // `tag` is repeatable, so the whole set is rewritten rather than appended to.
  const setTags = (next: string[]) => {
    const params = new URLSearchParams(searchParams);
    params.delete('tag');
    for (const id of next) params.append('tag', id);
    setSearchParams(params, { replace: true });
  };

  const setTagMatch = (next: 'any' | 'all') => {
    const params = new URLSearchParams(searchParams);
    if (next === 'all') params.set('tagMatch', 'all');
    else params.delete('tagMatch');
    setSearchParams(params, { replace: true });
  };

  // What the empty state has to distinguish: "you have not billed anybody yet"
  // from "nothing matches what you asked for".
  const filtered = status !== 'all' || !!search || !!projectId || tagIds.length > 0 || advanced.active;

  return (
    <>
      <PageHeader
        title="Invoices"
        subtitle="What you have billed, and what is still outstanding."
        actions={
          <>
            {/* The originals of imported invoices, in bulk - the import tool
                cannot carry a real PDF, so the person does; see
                AttachOriginalsSheet. Offered to anyone who may attach. */}
            {perms.can('attachment', 'create') ? (
              <Button type="button" variant="outline" size="sm" onClick={() => setAttachingOriginals(true)}>
                <Paperclip className="size-4" aria-hidden />
                Attach originals
              </Button>
            ) : null}
            {perms.can('invoice', 'create') ? (
              <Button asChild size="sm">
                <Link to="/invoices/new">
                  <Plus className="size-4" aria-hidden />
                  New invoice
                </Link>
              </Button>
            ) : null}
          </>
        }
      />

      {attachingOriginals ? <AttachOriginalsSheet onClose={() => setAttachingOriginals(false)} /> : null}

      <div className="space-y-4 px-5 pb-8">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1 max-w-sm">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <Input
              className="pl-8"
              placeholder="Search by number, reference or customer"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search invoices"
            />
          </div>
          <div className="flex flex-wrap gap-1">
            {FILTERS.map((filter) => (
              <button
                key={filter.value}
                type="button"
                onClick={() => setStatus(filter.value)}
                aria-pressed={status === filter.value}
                className={
                  status === filter.value
                    ? 'inline-flex min-w-11 items-center justify-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary touch:min-h-11 touch:py-0'
                    : 'inline-flex min-w-11 items-center justify-center rounded-full px-3 py-1 text-xs text-muted-foreground hover:bg-accent active:bg-accent touch:min-h-11 touch:py-0'
                }
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1" role="group" aria-label="Invoice date">
          {periods.map((period) => {
            const on = activePeriod?.key === period.key;
            return (
              <button
                key={period.key}
                type="button"
                onClick={() => setPeriod(on ? null : period)}
                aria-pressed={on}
                title={`${formatDate(period.from)} to ${formatDate(period.to)}`}
                data-testid={`invoice-period-${period.key}`}
                className={
                  on
                    ? 'inline-flex min-w-11 items-center justify-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary touch:min-h-11 touch:py-0'
                    : 'inline-flex min-w-11 items-center justify-center rounded-full border border-border px-3 py-1 text-xs text-muted-foreground hover:bg-accent active:bg-accent touch:min-h-11 touch:py-0'
                }
              >
                {period.short}
              </button>
            );
          })}
        </div>

        <InvoiceFilters
          projectId={projectId}
          onProject={setProject}
          tagIds={tagIds}
          onTags={setTags}
          tagMatch={tagMatch}
          onTagMatch={setTagMatch}
        />

        <AdvancedFilter entityType="invoice" value={advanced.value} onChange={advanced.set} />

        {selectedIds.length > 0 ? (
          <div
            className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2"
            data-testid="invoice-selection-bar"
          >
            <p className="text-sm">
              <strong className="font-medium">{selectedIds.length} selected</strong>
              {query.hasNextPage ? (
                <span className="text-muted-foreground"> — of the invoices loaded; more match below the list.</span>
              ) : null}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" onClick={() => download('pdf')} disabled={!!downloading || tooMany} data-testid="invoice-download-pdf">
                <Download className="size-4" aria-hidden />
                {downloading === 'pdf' ? 'Preparing…' : 'One PDF'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => download('zip')} disabled={!!downloading || tooMany} data-testid="invoice-download-zip">
                <Archive className="size-4" aria-hidden />
                {downloading === 'zip' ? 'Preparing…' : 'ZIP of PDFs'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])}>
                <X className="size-4" aria-hidden />
                Clear
              </Button>
            </div>
            {tooMany ? (
              <p className="w-full text-xs text-muted-foreground">
                One download takes {BULK_PDF_MAX} invoices. Pick a month above and download it on its own.
              </p>
            ) : null}
            {downloadError ? (
              <p role="alert" className="w-full text-xs text-destructive">
                {downloadError}
              </p>
            ) : null}
          </div>
        ) : null}

        <Card>
          {query.isError ? (
            <ErrorState error={query.error} onRetry={() => query.refetch()} />
          ) : loading ? (
            <SkeletonTable rows={6} cols={5} />
          ) : rows.length ? (
            <>
            {byExternalNumber.length ? (
              <p role="status" className="border-b border-border px-4 py-2 text-xs text-muted-foreground" data-testid="invoices-by-external-number">
                Matched by the number it had before, among the invoices loaded here.
              </p>
            ) : null}
            {/* Phone: one card per invoice, with the figures on it. */}
            {isPhone ? (
            <Cards>
              {rows.map((invoice) => (
                <CardRow key={invoice.id} data-testid="invoice-row" className="flex items-start gap-1">
                  <label className="-ml-2.5 -mt-1.5 flex size-11 shrink-0 cursor-pointer items-center justify-center">
                    <input
                      type="checkbox"
                      className="size-4 rounded border-input"
                      aria-label={`Select ${invoice.number}`}
                      checked={selected.has(invoice.id)}
                      onChange={() => toggleOne(invoice.id)}
                    />
                  </label>
                  <Link to={`/invoices/${invoice.id}`} className="block min-w-0 flex-1">
                    <CardLine
                      title={invoice.customerName}
                      meta={[invoice.number, originallyTitle(invoice.externalNumber, invoice.sourceSystem, invoice.number)].filter(Boolean).join(' · ')}
                      value={<Money value={invoice.total} currency={invoice.currency} />}
                      sub={
                        // The balance only when it differs from the total.
                        // Repeating the same figure twice on a small screen is
                        // noise; a balance that is LESS is the fact worth a line.
                        invoice.balanceDue !== invoice.total ? (
                          <>
                            <Money value={invoice.balanceDue} currency={invoice.currency} /> due
                          </>
                        ) : null
                      }
                    />
                    <CardFacts>
                      <StatusBadge status={invoice.status} />
                      <span>{formatDate(invoice.date)}</span>
                      {invoice.dueDate ? <span>due {formatDate(invoice.dueDate)}</span> : null}
                      {invoice.projectName ? <span>{invoice.projectName}</span> : null}
                    </CardFacts>
                    {invoice.tags.length ? (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {invoice.tags.map((tag) => (
                          <ColourChip key={tag.id} colour={tag.colour} name={tag.name} />
                        ))}
                      </div>
                    ) : null}
                  </Link>
                </CardRow>
              ))}
            </Cards>
            ) : (

            <Table>
              <THead>
                <TR>
                  <TH className="w-10">
                    {/* A 44px label around the box: the cell is the target. */}
                    <label className="-m-2.5 flex size-11 cursor-pointer items-center justify-center">
                      <input
                        type="checkbox"
                        className="size-4 rounded border-input"
                        aria-label={`Select the ${loadedIds.length} invoices shown`}
                        data-testid="invoice-select-all"
                        checked={allLoadedSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someLoadedSelected && !allLoadedSelected;
                        }}
                        onChange={toggleAllLoaded}
                      />
                    </label>
                  </TH>
                  <TH>Number</TH>
                  <TH>Customer</TH>
                  <TH>Date</TH>
                  <TH>Due</TH>
                  {/*
                    Hidden below `lg`. On an iPad held upright the rail leaves
                    688px, and eight columns put Status, Total and Balance -
                    the figures the list is for - behind the scroll fade. The
                    classification is the column a person can do without at
                    that width; the invoice itself still shows it.
                  */}
                  <TH className="hidden lg:table-cell">Project &amp; tags</TH>
                  <TH>Status</TH>
                  <TH numeric>Total</TH>
                  <TH numeric>Balance</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((invoice) => (
                  <TR key={invoice.id} data-testid="invoice-row">
                    <TD className="w-10">
                      <label className="-m-2.5 flex size-11 cursor-pointer items-center justify-center">
                        <input
                          type="checkbox"
                          className="size-4 rounded border-input"
                          aria-label={`Select ${invoice.number}`}
                          checked={selected.has(invoice.id)}
                          onChange={() => toggleOne(invoice.id)}
                        />
                      </label>
                    </TD>
                    <TD>
                      <Link
                        to={`/invoices/${invoice.id}`}
                        className="font-medium text-primary hover:underline"
                        data-tap-exempt="text link in a table cell; the hit area is drawn around it"
                      >
                        {invoice.number}
                      </Link>
                      {/* The number it had in the system it came from, under
                          Fint's own - the one the customer's accountant quotes. */}
                      {originallyTitle(invoice.externalNumber, invoice.sourceSystem, invoice.number) ? (
                        <div className="text-xs text-muted-foreground" data-testid="invoice-originally">
                          {originallyTitle(invoice.externalNumber, invoice.sourceSystem, invoice.number)}
                        </div>
                      ) : null}
                    </TD>
                    <TD>{invoice.customerName}</TD>
                    <TD className="text-muted-foreground">{formatDate(invoice.date)}</TD>
                    <TD className="text-muted-foreground">{formatDate(invoice.dueDate)}</TD>
                    <TD className="hidden lg:table-cell">
                      {/* Read-only here. Changing an issued invoice's
                          classification goes through the invoice itself, which
                          is where the project-and-customer rule that the API
                          enforces can be explained rather than just refused. */}
                      {invoice.projectName || invoice.tags.length ? (
                        <div className="flex flex-wrap items-center gap-1.5">
                          {invoice.projectName ? (
                            <span className="text-xs text-muted-foreground">{invoice.projectName}</span>
                          ) : null}
                          {invoice.tags.map((tag) => (
                            <ColourChip key={tag.id} colour={tag.colour} name={tag.name} />
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">&mdash;</span>
                      )}
                    </TD>
                    <TD>
                      <StatusBadge status={invoice.status} />
                    </TD>
                    <TD numeric>
                      <Money value={invoice.total} currency={invoice.currency} />
                    </TD>
                    <TD numeric>
                      <Money value={invoice.balanceDue} currency={invoice.currency} />
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            )}
            {/* The count is of the rows on screen - the fallback's when it is
                the fallback that is on screen; a page of it has nothing more to load. */}
            <ListFooter
              query={byExternalNumber.length ? { items: rows, hasNextPage: false, isFetchingNextPage: false, fetchNextPage: () => {} } : query}
              noun="invoices"
            />
            </>
          ) : (
            <EmptyState
              icon={FileText}
              title={filtered ? 'Nothing matches that' : 'No invoices yet'}
              description={
                filtered
                  ? 'Try a different filter or search term.'
                  : 'Raise your first invoice and it will appear here.'
              }
              action={
                !filtered && perms.can('invoice', 'create') ? (
                  <Button asChild size="sm">
                    <Link to="/invoices/new">New invoice</Link>
                  </Button>
                ) : null
              }
            />
          )}
        </Card>
      </div>
    </>
  );
}

/**
 * Project and tag, as controls rather than as query parameters somebody has to
 * know about. The same pair the expense list carries, deliberately in the same
 * order, with the same wording and the same `none` option - a filter row that
 * behaves differently on two screens is one somebody has to learn twice.
 *
 * ── WHY EACH ONE IS NAMED "FILTER BY …" ──
 * "Project" and "Tags" are also the names of fields in the invoice composer.
 * To a screen reader moving through the app that would be two names for four
 * controls, so these say what they filter.
 *
 * The Any/All toggle appears only once two tags are chosen, because with one
 * tag the two modes are the same question and a control that cannot change the
 * answer is a control that costs a decision.
 */
function InvoiceFilters({
  projectId,
  onProject,
  tagIds,
  onTags,
  tagMatch,
  onTagMatch,
}: {
  projectId: string;
  onProject: (value: string) => void;
  tagIds: string[];
  onTags: (value: string[]) => void;
  tagMatch: 'any' | 'all';
  onTagMatch: (value: 'any' | 'all') => void;
}) {
  const projects = useProjects();
  const tags = useTags();

  const active = !!projectId || tagIds.length > 0;
  const chosenTags = (tags.data?.items ?? []).filter((tag) => tagIds.includes(tag.id));

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label="Filter by project"
          value={projectId}
          onChange={(event) => onProject(event.target.value)}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">Project: all</option>
          {/* A value, not the absence of one: this is the list that tells
              somebody how much of their billing is still unclassified. */}
          <option value="none">Not on a project</option>
          {(projects.data?.items ?? []).map((project) => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>

        <select
          aria-label="Filter by tag"
          value=""
          onChange={(event) => {
            const id = event.target.value;
            if (id && !tagIds.includes(id)) onTags([...tagIds, id]);
          }}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">
            {tagIds.length === 0 ? 'Tags: all' : `Tags: ${tagIds.length} selected`}
          </option>
          {(tags.data?.items ?? [])
            .filter((tag) => !tagIds.includes(tag.id))
            .map((tag) => (
              <option key={tag.id} value={tag.id}>
                {tag.name}
              </option>
            ))}
        </select>

        {tagIds.length > 1 ? (
          <div role="group" aria-label="Match tags" className="flex gap-1">
            {(
              [
                ['any', 'Any of these'],
                ['all', 'All of these'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                aria-pressed={tagMatch === key}
                onClick={() => onTagMatch(key)}
                className={
                  tagMatch === key
                    ? 'rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary'
                    : 'rounded-full px-3 py-1 text-xs text-muted-foreground hover:bg-accent'
                }
              >
                {label}
              </button>
            ))}
          </div>
        ) : null}

        {active ? (
          <button
            type="button"
            onClick={() => {
              onProject('');
              onTags([]);
            }}
            className="text-xs text-primary hover:underline"
          >
            Clear filters
          </button>
        ) : null}
      </div>

      {/* The chosen tags render as removable chips below the row, so the filter
          is visible without opening the select that set it. */}
      {chosenTags.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {chosenTags.map((tag) => (
            <ColourChip
              key={tag.id}
              colour={tag.colour}
              name={tag.name}
              onRemove={() => onTags(tagIds.filter((id) => id !== tag.id))}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The periods the chips offer, newest first: this month, last month, this
 * quarter (the FY's own - a CA's "Q2" is Jul-Sep), this FY and last FY. From
 * the BOOK's today, so a month boundary is the client's, not the browser's.
 */
export function invoicePeriods(today: string, startMonth = 4): Array<Period & { short: string }> {
  const at = new Date(`${today}T12:00:00`);
  // This month, last month, this FY - in that order.
  const [thisMonth, lastMonth, thisFy] = quickPeriods(startMonth, at);
  const fy = financialYearOf(today, startMonth);
  const quarter = financialYearQuarters(fy.startYear, startMonth).find((q) => q.from <= today && today <= q.to);
  return [
    thisMonth,
    lastMonth,
    quarter ? { ...quarter, short: 'This quarter' } : undefined,
    thisFy,
    { ...financialYearPeriod(fy.startYear - 1, startMonth), short: 'Last FY' },
  ].filter((period): period is Period & { short: string } => !!period);
}
